import { keepPreviousData, queryOptions, useQuery } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { api, type ListResponse } from "@/api/client"
import type { RunDetail } from "@/api/generated/RunDetail"
import type { RunSummary } from "@/api/generated/RunSummary"
import { useApiMutation } from "@/api/mutation"

export const RUN_STATUSES = ["running", "completed", "failed", "cancelled", "waiting"] as const
export const RUN_SOURCES = [
  "chat",
  "playground",
  "api",
  "endpoint",
  "automation",
  "schedule",
  "workplace",
  "task",
  "eval",
] as const

export interface RunFilters {
  limit: number
  offset: number
  agent_id?: number
  source?: string
  status?: string
  context_id?: string
  q?: string
  /** ISO timestamp (inclusive) */
  from?: string
  /** ISO timestamp (exclusive) */
  to?: string
}

export const runKeys = {
  all: ["runs"] as const,
  list: (f: RunFilters) => [...runKeys.all, "list", f] as const,
  detail: (key: string) => [...runKeys.all, "detail", key] as const,
}

export const runsQuery = (f: RunFilters) =>
  queryOptions({
    queryKey: runKeys.list(f),
    queryFn: ({ signal }) => api.get<ListResponse<RunSummary>>("/runs", { signal, query: { ...f } }),
    placeholderData: keepPreviousData,
  })

export const useRuns = (f: RunFilters, opts: { refetchInterval?: number | false } = {}) =>
  useQuery({ ...runsQuery(f), refetchInterval: opts.refetchInterval })

export const runQuery = (key: string) =>
  queryOptions({
    queryKey: runKeys.detail(key),
    queryFn: ({ signal }) => api.get<RunDetail>(`/runs/${key}`, { signal }),
  })

export const useRun = (key: string | undefined) => useQuery({ ...runQuery(key ?? ""), enabled: !!key })

export function useCancelRun() {
  const { t } = useTranslation()
  return useApiMutation<void, string>({
    mutationFn: (uid) => api.post(`/runs/${uid}/cancel`),
    invalidates: [runKeys.all],
    successToast: () => t("insights.runs.cancelRequested"),
  })
}
